"use client";

import { Button } from "@/components/ui/button-pro";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";
import { Bell, Mail, Sparkles } from "lucide-react";
import { useState } from "react";
import { useToast } from "../hooks/use-toast";

export function Newsletter() {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // const [captchaVerified, setCaptchaVerified] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // if (!captchaVerified) {
    //   toast({
    //     title: "Verification Required",
    //     description: "Please complete the captcha verification first.",
    //     variant: "destructive",
    //   });
    //   return;
    // }

    setIsSubmitting(true);

    try {
      const response = await fetch("/api/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to subscribe");
      }

      toast({
        title: "Successfully subscribed!",
        description:
          "Thank you for subscribing to our newsletter. We've sent you a confirmation email.",
      });

      setEmail("");
      // setCaptchaVerified(false);
    } catch (error) {
      console.error("Error subscribing:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to subscribe. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  return (
    <section
      id="newsletter"
      className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 via-white to-pink-50 dark:from-purple-900/20 dark:via-gray-900 dark:to-pink-900/20"
    >
      <motion.div
        className="max-w-4xl mx-auto"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <motion.div className="text-center mb-12" variants={itemVariants}>
          <div className="flex items-center justify-center gap-3 mb-6">
            <Bell className="w-8 h-8 text-purple-600 dark:text-purple-400" />
            <h2 className="text-4xl sm:text-5xl font-bold">
              <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 dark:from-purple-400 dark:via-pink-400 dark:to-purple-400 bg-clip-text text-transparent">
                Stay Updated
              </span>
            </h2>
          </div>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Get the latest insights, project updates, and tech tips delivered to
            your inbox.
          </p>
        </motion.div>

        <motion.div variants={itemVariants}>
          <Card className="bg-white/80 dark:bg-gray-800/80 border-purple-500/20 backdrop-blur-sm shadow-2xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-pink-500/5"></div>

            <CardHeader className="text-center pb-8 relative z-10">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="p-3 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl">
                  <Mail className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
              <CardTitle className="text-3xl font-bold text-gray-900 dark:text-white">
                Subscribe to Newsletter
              </CardTitle>
              <CardDescription className="text-lg text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                Join our community and never miss out on the latest developments
                in web technology and design.
              </CardDescription>
            </CardHeader>

            <CardContent className="pb-8 relative z-10">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                  <Input
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-white/50 dark:bg-gray-700/50 border-2 border-gray-200 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 focus:border-purple-500 dark:focus:border-purple-400 rounded-xl h-12 text-base flex-1"
                    required
                  />
                  <Button
                    type="submit"
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Subscribing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        Subscribe
                      </>
                    )}
                  </Button>
                </div>

                {/* <div className="max-w-md mx-auto overflow-hidden">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 block">
                    Security Verification
                  </label>
                  <Captcha
                    onVerify={setCaptchaVerified}
                    className="bg-white/50 dark:bg-gray-700/50 border-2 border-gray-200 dark:border-gray-600 rounded-xl p-4"
                  />
                </div> */}

                <div className="text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No spam, unsubscribe at any time. We respect your privacy.
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        {/* Benefits */}
        <motion.div
          className="grid sm:grid-cols-3 gap-6 mt-12"
          variants={containerVariants}
        >
          {[
            {
              icon: Sparkles,
              title: "Latest Updates",
              description: "Stay informed about new technologies and trends",
            },
            {
              icon: Bell,
              title: "Project Insights",
              description: "Get behind-the-scenes looks at my latest work",
            },
            {
              icon: Mail,
              title: "Exclusive Content",
              description: "Access to tips, tutorials, and resources",
            },
          ].map((benefit, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="text-center p-6 bg-white/60 dark:bg-gray-800/60 rounded-2xl border border-purple-500/20 backdrop-blur-sm"
              whileHover={{ y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <div className="inline-flex p-3 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl mb-4">
                <benefit.icon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                {benefit.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
